/**
 * Line and area marks for the editor preview.
 *
 * Mirrors Chart\LineRenderer. These need a real path, so they get an SVG
 * stretched to the plot box with `preserveAspectRatio="none"`;
 * `vector-effect="non-scaling-stroke"` keeps the 2px stroke 2px through that
 * stretch, and markers are zero-length round-capped strokes so they stay
 * circles rather than becoming ellipses.
 */

import {
	areaPath,
	bandCenter,
	baselineFraction,
	linePath,
	linePoints,
	valueFraction,
} from './geometry';
import { formatValue } from './format';

/**
 * Values as plotted, which is the raw row unless the chart stacks.
 *
 * @param {Object} config Normalised config.
 * @return {Array} Value rows by series index.
 */
function plottedRows( config ) {
	const rows = config.series.map( ( series ) => [ ...series.data ] );

	if ( ! config.stacked ) {
		return rows;
	}

	const running = [];
	rows.forEach( ( values, index ) => {
		values.forEach( ( value, position ) => {
			running[ position ] = ( running[ position ] || 0 ) + value;
			rows[ index ][ position ] = running[ position ];
		} );
	} );

	return rows;
}

export function LineMarks( { config } ) {
	const { scale } = config;
	const isArea = config.type === 'area';
	const baseline = 100 - baselineFraction( scale ) * 100;
	const surface = config.surface || 'var(--chart-surface)';
	const rows = plottedRows( config );

	return (
		<svg
			className="brandy-chart__marks"
			viewBox="0 0 100 100"
			preserveAspectRatio="none"
			aria-hidden="true"
			focusable="false"
		>
			{ rows.map( ( values, index ) => {
				const points = linePoints( values, scale );
				const path = linePath( points, config.smooth );
				if ( ! path ) {
					return null;
				}
				const color = config.series[ index ].color;

				return (
					<g key={ index }>
						{ isArea && (
							<path
								className="brandy-chart__area"
								d={ areaPath( path, points, baseline ) }
								fill={ color }
								fillOpacity="0.1"
							/>
						) }
						<path
							className="brandy-chart__line"
							d={ path }
							fill="none"
							stroke={ color }
							strokeWidth="2"
							strokeLinejoin="round"
							strokeLinecap="round"
							vectorEffect="non-scaling-stroke"
						/>
					</g>
				);
			} ) }
			{ config.markers &&
				rows.map( ( values, index ) =>
					linePoints( values, scale ).map( ( point ) => {
						// A zero-length subpath with a round cap renders as a
						// dot, and non-scaling-stroke sizes it in device pixels
						// — so it stays a circle however the viewBox stretches.
						// The wider surface-coloured pass is the 2px ring.
						const dot = `M ${ point.x } ${ point.y } l 0 0`;
						return (
							<g key={ `${ index }-${ point.index }` }>
								<path
									className="brandy-chart__marker-ring"
									d={ dot }
									stroke={ surface }
									strokeWidth="12"
									strokeLinecap="round"
									vectorEffect="non-scaling-stroke"
									fill="none"
								/>
								<path
									className="brandy-chart__marker"
									d={ dot }
									stroke={ config.series[ index ].color }
									strokeWidth="8"
									strokeLinecap="round"
									vectorEffect="non-scaling-stroke"
									fill="none"
								/>
							</g>
						);
					} )
				) }
		</svg>
	);
}

export function PointLabels( { config } ) {
	if ( config.valueLabels === 'none' ) {
		return null;
	}

	const rows = plottedRows( config );
	const labels = [];

	rows.forEach( ( values, index ) => {
		// Selective means the endpoint: the value a trend line is read for.
		const only =
			config.valueLabels === 'selective' ? values.length - 1 : null;

		values.forEach( ( value, position ) => {
			if ( only !== null && position !== only ) {
				return;
			}
			labels.push(
				<span
					key={ `${ index }-${ position }` }
					className="brandy-chart__point-label"
					style={ {
						'--label-x': `${
							bandCenter( position, values.length ) * 100
						}%`,
						'--label-y': `${
							valueFraction( value, config.scale ) * 100
						}%`,
					} }
				>
					{ formatValue(
						config.series[ index ].data[ position ],
						config.format
					) }
				</span>
			);
		} );
	} );

	if ( ! labels.length ) {
		return null;
	}

	return <div className="brandy-chart__point-labels">{ labels }</div>;
}
